import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { analyticsApi } from '@/lib/api'
import KpiCard from './KpiCard'
import { EmptyChart } from './EmptyChart'
import { ChartBar } from '@phosphor-icons/react'
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import { useReducedMotion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { cardTitleStyle } from '@/lib/styleKit'
import { DotLoader } from '@/components/ui/DotLoader'
import { Select } from '@/components/ui/Select'
import {
  TOP_CAMPAIGN_METRIC_OPTIONS,
  topCampaignBadge,
  topCampaignDetail,
  type TopCampaignMetric,
} from './topCampaignMetric'
import {
  BRAND_SERIES_COLOR,
  sequentialStep,
  seriesColor,
  seriesDash,
  tooltipContentStyle,
  useChartTheme,
} from './chartTheme'
import { sentimentDonutData } from '@/lib/sentiment'
import type { OverviewData, Period } from '@/types/analytics'

interface TabProps {
  period: Period | null
  startDate: string | null
  endDate: string | null
  userId: number | null
}

// The pipeline ramp this file declared — `['#5e6ad2','#7c87dd','#9aa3e8',
// '#bcc2ee','#d4d8f3','#e5e7f8']` — measured 1.2:1, 1.4:1 and 1.7:1 against a
// white card on its last three steps, so half the funnel was invisible in light
// mode. It is now `chartTheme`'s ordinal ramp, which is bounded at 3:1 in both
// modes and is the same ramp the Contacts funnel uses (§61).

// One hue per metric, decided once in `chartTheme` — "replies" is this colour in
// the campaign drilldown too, and the dash pattern carries the encoding a second
// way for the pairs colour alone cannot separate.
const TREND_SERIES = [
  { key: 'opens',   label: 'Opens' },
  { key: 'clicks',  label: 'Clicks' },
  { key: 'replies', label: 'Replies' },
] as const

const cardCls = cn(
  'rounded-xl border transition-colors duration-150',
  'bg-card border-border/50',
  'dark:bg-white/[0.02] dark:border-white/[0.08]',
)

export default function OverviewTab({ period, startDate, endDate, userId }: TabProps) {
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const [topMetric, setTopMetric] = useState<TopCampaignMetric>('engagement')
  const { data, isLoading } = useQuery<OverviewData>({
    queryKey: ['analytics', 'overview', period, startDate, endDate, userId, topMetric],
    keepPreviousData: true,
    queryFn: () =>
      analyticsApi
        .getOverview({ period: period || undefined, start: startDate || undefined, end: endDate || undefined, user_id: userId ?? undefined, top_metric: topMetric })
        .then((r) => r.data),
    staleTime: 5 * 1000,
    refetchOnWindowFocus: true,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#5e6ad2] dark:text-[#a5b4fc]">
        <DotLoader size="lg" label="Loading analytics overview" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center max-w-md mx-auto">
        <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-4">
          <ChartBar className="h-5 w-5 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
        </div>
        <h3
          className="text-foreground dark:text-[#f7f8f8] mb-2"
          style={{ fontSize: '20px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.33 }}
        >
          No analytics data
        </h3>
        <p
          className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
          style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
        >
          No analytics data available for this period.
        </p>
      </div>
    )
  }

  // Hue and label come from `lib/sentiment` (§10.1) — the reply-sentiment palette
  // this file used to declare now lives there, same ruling (neutral is brand
  // indigo, amber stays a trust signal) but one owner, so a sentiment cannot be
  // two colours across the Replies tab and the Inbox. Slices follow the shared
  // SENTIMENT_ORDER rather than the API's key order, so every surface lists
  // sentiments in the same sequence and the two grays stay away from neutral.
  // The mapping itself now lives in lib/sentiment, shared with the campaign
  // detail doughnut, so the two cannot drift on colour or order.
  const sentimentData = sentimentDonutData(data.reply_sentiment)

  const sentimentTotal = sentimentData.reduce((s, d) => s + d.value, 0)
  // Zero buckets are kept OUT of the arc — recharts lays out a zero-value
  // sector and `paddingAngle` pads it into a visible sliver of that bucket's
  // hue — but stay IN the legend below, so a bucket with no replies this period
  // still reports itself rather than vanishing (an absent row reads as "not
  // tracked" rather than "none"). Same split as the Replies tab.
  const sentimentSlices = sentimentData.filter((d) => d.value > 0)

  return (
    <div className="space-y-5">
      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {/* Unified indigo sparkline across the row — KPI hierarchy comes from
            label + value, not from a different hue per metric. */}
        <KpiCard label="Emails sent" hint="Every email that left a mailbox in this period, including follow-ups. One contact who gets 3 emails counts 3 times. Bounced and failed sends are included." metric={data.kpis.emails_sent}  sparkline={data.kpi_sparklines.emails_sent}  format="number"  color={BRAND_SERIES_COLOR} />
        <KpiCard label="Open rate"    metric={data.kpis.open_rate}    sparkline={data.kpi_sparklines.open_rate}    format="percent" color={BRAND_SERIES_COLOR} />
        <KpiCard label="Click rate"   metric={data.kpis.click_rate}   sparkline={data.kpi_sparklines.click_rate}   format="percent" color={BRAND_SERIES_COLOR} />
        <KpiCard label="Reply rate"   metric={data.kpis.reply_rate}   sparkline={data.kpi_sparklines.reply_rate}   format="percent" color={BRAND_SERIES_COLOR} />
        <KpiCard label="Bounce rate"  metric={data.kpis.bounce_rate}  sparkline={data.kpi_sparklines.bounce_rate}  format="percent" invertColor color={BRAND_SERIES_COLOR} />
      </div>

      {/* Row 2: Engagement Trends + Sentiment */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className={cn(cardCls, 'lg:col-span-2')}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Engagement trends
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.engagement_trends.length > 0 ? (
              // `role="group"`, not a bare labelled div: `aria-label` on a
              // generic div is dropped by AT, and `role="img"` would hide the
              // series `accessibilityLayer` just made explorable.
              <div role="group" aria-label="Opens, clicks and replies per day">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart accessibilityLayer data={data.engagement_trends} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} />
                  <Tooltip
                    cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                    contentStyle={tooltipContentStyle(chartTheme)}
                    labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                  {TREND_SERIES.map(({ key, label }) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={label}
                      stroke={seriesColor(key)}
                      strokeDasharray={seriesDash(key)}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={!prefersReduced}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              </div>
            ) : (
              <EmptyChart message="No engagement data yet." />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Reply sentiment
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sentimentTotal > 0 ? (
              <div className="flex flex-col items-center" role="group" aria-label="Reply sentiment breakdown">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart accessibilityLayer>
                    <Pie
                      data={sentimentSlices}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      isAnimationActive={!prefersReduced}
                    >
                      {sentimentSlices.map((entry) => (
                        <Cell key={entry.key} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipContentStyle(chartTheme)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-2.5 mt-3 justify-center">
                  {sentimentData.map((d) => (
                    <div
                      key={d.key}
                      className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                      style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={
                          d.value > 0
                            ? { backgroundColor: d.color }
                            : { boxShadow: `inset 0 0 0 1px ${d.color}`, opacity: 0.5 }
                        }
                      />
                      <span className={d.value > 0 ? undefined : 'opacity-60'}>{d.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyChart message="No reply sentiment data yet." />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Top Campaigns + Pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className={cardCls}>
          <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Top campaigns
            </CardTitle>
            <Select
              ariaLabel="Rank top campaigns by"
              value={topMetric}
              onChange={(v) => setTopMetric(v as TopCampaignMetric)}
              options={TOP_CAMPAIGN_METRIC_OPTIONS}
              className="w-44"
            />
          </CardHeader>
          <CardContent>
            {data.top_campaigns.length > 0 ? (
              <div className="space-y-1.5">
                {data.top_campaigns.map((c, i) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded-md px-2.5 py-2 hover:bg-muted/40 dark:hover:bg-white/[0.04] transition-colors duration-150"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className={cn(
                          'inline-flex items-center justify-center h-6 w-6 rounded-full border tabular-nums',
                          'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/20',
                          'dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/25',
                        )}
                        style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.1px' }}
                      >
                        {i + 1}
                      </span>
                      <span
                        className="truncate text-foreground dark:text-[#f7f8f8]"
                        style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                      >
                        {c.name}
                      </span>
                      {topCampaignDetail(c, topMetric) && (
                        <span
                          className="hidden sm:inline whitespace-nowrap text-muted-foreground dark:text-[#8a8f98]"
                          style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                        >
                          {topCampaignDetail(c, topMetric)}
                        </span>
                      )}
                    </div>
                    <span
                      className={cn(
                        'whitespace-nowrap ml-3 inline-flex items-center px-2 py-0.5 rounded-full border tabular-nums',
                        'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25',
                        'dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30',
                      )}
                      style={{ fontSize: '11px', fontWeight: 590, letterSpacing: '-0.05px' }}
                    >
                      {topCampaignBadge(c, topMetric)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyChart message="No campaign engagement in this period yet." />
            )}
          </CardContent>
        </Card>

        <Card className={cardCls}>
          <CardHeader>
            <CardTitle className="text-foreground dark:text-[#f7f8f8]" style={cardTitleStyle}>
              Contact pipeline
            </CardTitle>
            <p
              className="text-muted-foreground dark:text-[#8a8f98] mt-1"
              style={{ fontSize: '11px', letterSpacing: '-0.05px', lineHeight: 1.5 }}
            >
              Counts people, not emails, for contacts added in the period above, by their current stage. Each
              contact is in one stage, so &quot;Contacted&quot; excludes people who have since moved to Engaged or
              Qualified, and it will not match Emails sent.
            </p>
          </CardHeader>
          <CardContent>
            {data.pipeline.stages.length > 0 ? (
              <div className="space-y-3">
                {data.pipeline.stages.map((stage, i) => (
                  <div key={stage.name} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-foreground dark:text-[#f7f8f8] capitalize"
                        style={{ fontSize: '12px', fontWeight: 510, letterSpacing: '-0.1px' }}
                      >
                        {stage.name.replace(/_/g, ' ')}
                      </span>
                      <span
                        className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '11px', letterSpacing: '-0.1px' }}
                      >
                        {stage.count.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden bg-muted/50 dark:bg-white/[0.06]">
                      <div
                        className="h-full rounded-full transition-all duration-300"
                        style={{
                          width: `${Math.min(Math.max(stage.pct, 0), 100)}%`,
                          backgroundColor: sequentialStep(chartTheme, i, data.pipeline.stages.length),
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyChart message="No pipeline data yet." />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
